import JSBI from 'jsbi';
import { ZERO } from '../../constants';
import { Percentage } from '../../utils';
import { addDecimals, changeDecimals, removeDecimals } from '../../utils/math';
import { RefFinanceStablePool } from './types';

export const calc_d = ({ pool }: { pool: RefFinanceStablePool }) => {
  const { amp, cAmountReserves } = pool;

  const token_num = cAmountReserves.size;
  const numReserves = Array.from(cAmountReserves.values()).map(
    (v) => +v.toString()
  );
  const sum_amounts = numReserves.reduce((acc, amount) => acc + amount, 0);

  let d_prev = 0;
  let d = sum_amounts;
  for (let i = 0; i < 256; i++) {
    let d_prod = d;

    for (const c_amount of numReserves) {
      d_prod = (d_prod * d) / (c_amount * token_num);
    }

    d_prev = d;

    const ann = amp * token_num ** token_num;
    const numerator = d_prev * (d_prod * token_num + ann * sum_amounts);
    const denominator = d_prev * (ann - 1) + d_prod * (token_num + 1);
    d = numerator / denominator;

    if (Math.abs(d - d_prev) <= 1) break;
  }

  return d;
};

export const calc_y = ({
  pool,
  x_c_amount,
  tokenInId,
  tokenOutId,
}: {
  pool: RefFinanceStablePool;
  x_c_amount: number;
  tokenInId: string;
  tokenOutId: string;
}) => {
  const token_num = pool.cAmountReserves.size;
  const ann = pool.amp * token_num ** token_num;
  const d = calc_d({ pool });
  let s = x_c_amount;
  let c = (d * d) / x_c_amount;

  // need for 3-token pools
  for (const [token, amount] of pool.cAmountReserves) {
    if (token !== tokenInId && token !== tokenOutId) {
      const numAmount = +amount.toString();
      s += numAmount;
      c = (c * d) / numAmount;
    }
  }

  c = (c * d) / (ann * token_num ** token_num);
  const b = d / ann + s;
  let y_prev = 0;
  let y = d;

  for (let i = 0; i < 256; i++) {
    y_prev = y;
    const y_numerator = y ** 2 + c;
    const y_denominator = 2 * y + b - d;
    y = y_numerator / y_denominator;
    if (Math.abs(y - y_prev) <= 1) break;
  }

  return y;
};

const FEE_DIVISOR = 10000;

const tradeFee = (amount: JSBI, trade_fee: JSBI) => {
  return JSBI.divide(
    JSBI.multiply(amount, trade_fee),
    JSBI.BigInt(FEE_DIVISOR)
  );
};

export function calc_swap({
  pool,
  tokenInId,
  tokenOutId,
  amountIn,
}: {
  pool: RefFinanceStablePool;
  tokenInId: string;
  tokenOutId: string;
  amountIn: JSBI;
}): [JSBI, JSBI] {
  const inputTokenBalance = pool.cAmountReserves.get(tokenInId) || '0';

  const y = calc_y({
    pool,
    x_c_amount: +JSBI.add(amountIn, JSBI.BigInt(inputTokenBalance)).toString(),
    tokenInId,
    tokenOutId,
  });

  const outputTokenBalance = pool.cAmountReserves.get(tokenOutId) || '0';
  const amountOut = JSBI.BigInt(+outputTokenBalance.toString() - y);

  const fee = tradeFee(amountOut, JSBI.BigInt(pool.total_fee));
  const amountOutLessFees = JSBI.subtract(amountOut, fee);

  return [amountOutLessFees, fee];
}

// TODO: add output types
export function getStableOutputAmount({
  tokenInId,
  tokenOutId,
  slippage,
  amountIn,
  stablePool,
}: {
  tokenInId: string;
  tokenOutId: string;
  slippage: Percentage;
  amountIn: JSBI;
  stablePool: RefFinanceStablePool;
}) {
  // hardcoded decimals for tokens in pool
  const STABLE_LP_TOKEN_DECIMALS =
    stablePool.pool_kind === 'STABLE_SWAP' ? 18 : 24;

  const inputTokenDecimals = stablePool.decimals.get(tokenInId) || null;
  const outputTokenDecimals = stablePool.decimals.get(tokenOutId) || null;

  if (inputTokenDecimals === null || outputTokenDecimals === null) {
    console.error({
      inputTokenDecimals,
      outputTokenDecimals,
      stablePool,
      tokenInId,
      tokenOutId,
    });
    throw new Error('No info about decimals of tokens');
  }

  // amount * rate / stable lp
  const updatedCReservesNum = Array.from(
    stablePool.cAmountReserves.entries()
  ).map<[string, JSBI]>(([tokenId, amount]) => {
    const rate = stablePool.rates.get(tokenId) || null;

    if (!rate) {
      throw new Error('No rate for token');
    }

    return [
      tokenId,
      removeDecimals(JSBI.multiply(amount, rate), STABLE_LP_TOKEN_DECIMALS),
    ];
  });

  const updatedCReserves = updatedCReservesNum.reduce<Map<string, JSBI>>(
    (acc, [tokenId, amount]) => acc.set(tokenId, amount),
    new Map()
  );

  const updatedStablePool: RefFinanceStablePool = {
    ...stablePool,
    cAmountReserves: updatedCReserves,
  };

  // change decimals from one to another
  const amountInWithHardcodedDecimals = changeDecimals(
    amountIn,
    inputTokenDecimals,
    STABLE_LP_TOKEN_DECIMALS
  );

  const inputPoolBalance = updatedCReserves.get(tokenInId) || ZERO;
  const outputPoolBalance = updatedCReserves.get(tokenOutId) || ZERO;

  if (
    JSBI.equal(inputPoolBalance, ZERO) ||
    JSBI.equal(outputPoolBalance, ZERO)
  ) {
    return {
      amountIn,
      amountOut: ZERO,
      minAmountOut: ZERO,
      feeAmount: ZERO,
      notEnoughLiquidity: true,
      priceImpact: 0,
    };
  }

  const [amount_swapped, fee] = calc_swap({
    tokenInId,
    amountIn: amountInWithHardcodedDecimals,
    tokenOutId,
    pool: updatedStablePool,
  });

  const rateIn = stablePool.rates.get(tokenInId) || null;
  const rateOut = stablePool.rates.get(tokenOutId) || null;

  if (!rateIn || !rateOut) {
    throw new Error('No rate for in or out token');
  }

  const amountOutWithRate = JSBI.divide(
    addDecimals(amount_swapped, STABLE_LP_TOKEN_DECIMALS),
    rateOut
  );

  const feeWithRate = JSBI.divide(
    addDecimals(fee, STABLE_LP_TOKEN_DECIMALS),
    rateOut
  );

  const amountOutWithTokenDecimals = changeDecimals(
    amountOutWithRate,
    STABLE_LP_TOKEN_DECIMALS,
    outputTokenDecimals
  );

  const feeAmountWithTokenDecimals = changeDecimals(
    feeWithRate,
    STABLE_LP_TOKEN_DECIMALS,
    outputTokenDecimals
  );

  const minAmountOut = slippage.getFor(amountOutWithTokenDecimals);

  const marketPrice = +rateOut.toString() / +rateIn.toString();
  const newMarketPrice =
    +amountInWithHardcodedDecimals.toString() / +amount_swapped.toString();
  const priceImpact = (newMarketPrice - marketPrice) / newMarketPrice;

  return {
    notEnoughLiquidity: false,
    amountIn,
    amountOut: amountOutWithTokenDecimals,
    minAmountOut,
    feeAmount: feeAmountWithTokenDecimals,
    priceImpact,
  };
}

// TODO: separate & pass swap func as param
const createSwapOptions = (params: {
  stablePool: RefFinanceStablePool;
  tokenInId: string;
  tokenOutId: string;
  slippage: Percentage;
  minAmount: JSBI;
  maxAmount: JSBI;
  numberOfSteps?: number;
}) => {
  const {
    stablePool,
    tokenInId,
    tokenOutId,
    slippage,
    minAmount,
    maxAmount,
    numberOfSteps = 200,
  } = params;

  const stepSize = JSBI.divide(
    JSBI.subtract(maxAmount, minAmount),
    JSBI.BigInt(numberOfSteps)
  );

  const swapOptions = Array.from(Array(numberOfSteps).keys())
    .map((v) => v + 1)
    .map((iteration) => {
      const amountIn = JSBI.add(
        minAmount,
        JSBI.multiply(stepSize, JSBI.BigInt(iteration))
      );
      const {
        notEnoughLiquidity,
        amountOut,
        minAmountOut,
        feeAmount,
        priceImpact,
      } = getStableOutputAmount({
        amountIn,
        tokenOutId,
        tokenInId,
        slippage,
        stablePool,
      });

      return {
        priceImpact,
        notEnoughLiquidity,
        amountIn,
        amountOut,
        minAmountOut,
        feeAmount,
      };
    });

  return swapOptions;
};

const findBestOption = (params: {
  stablePool: RefFinanceStablePool;
  tokenInId: string;
  tokenOutId: string;
  outputAmount: JSBI;
  slippage: Percentage;
  appproxInputAmount: JSBI;
  minAmountMultiplier?: number;
  maxAmountMultiplier?: number;
  numberOfSteps?: number;
}) => {
  const {
    stablePool,
    tokenInId,
    tokenOutId,
    outputAmount,
    slippage,
    appproxInputAmount,
    minAmountMultiplier = 0,
    maxAmountMultiplier = 2,
    numberOfSteps = 200,
  } = params;

  const minAmount = removeDecimals(
    JSBI.multiply(
      appproxInputAmount,
      JSBI.BigInt(minAmountMultiplier * 10 ** 3)
    ),
    3
  );

  const maxAmount = removeDecimals(
    JSBI.multiply(
      appproxInputAmount,
      JSBI.BigInt(maxAmountMultiplier * 10 ** 3)
    ),
    3
  );

  // create array of results swapping tokenA-tokenB and tokenB-tokenA
  const swapResults = createSwapOptions({
    stablePool,
    tokenInId,
    tokenOutId,
    slippage,
    minAmount,
    maxAmount,
    numberOfSteps,
  });

  // take the closes one to pool amount ratio - user amount ratio
  const swapAmounts = swapResults
    .map((ratios) => {
      const { amountOut } = ratios;

      const swapAmountOutDiff = JSBI.subtract(amountOut, outputAmount);

      const absDiff = JSBI.lessThan(swapAmountOutDiff, ZERO)
        ? JSBI.unaryMinus(swapAmountOutDiff)
        : swapAmountOutDiff;

      return { ...ratios, swapAmountOutDiff: absDiff };
    })
    .sort((ratioA, ratioB) =>
      JSBI.equal(ratioA.swapAmountOutDiff, ratioB.swapAmountOutDiff)
        ? 0
        : JSBI.lessThan(ratioA.swapAmountOutDiff, ratioB.swapAmountOutDiff)
        ? -1
        : 1
    );

  return swapAmounts[0];
};

// TODO: update this method to do it by calculations, not searching the best match
export const getStableInputAmount = ({
  tokenInId,
  tokenOutId,
  amountOut,
  slippage,
  stablePool,
}: {
  tokenInId: string;
  tokenOutId: string;
  amountOut: JSBI;
  slippage: Percentage;
  stablePool: RefFinanceStablePool;
}) => {
  // we expect that it's almost the same
  const appproxInputAmount = amountOut;

  let bestOption = null;
  const numberOfIterations = 2;

  for (let i = 0; i < numberOfIterations; i += 1) {
    if (!bestOption) {
      bestOption = findBestOption({
        stablePool,
        tokenInId,
        tokenOutId,
        slippage,
        outputAmount: amountOut,
        appproxInputAmount,
        numberOfSteps: 200,
      });
    } else {
      bestOption = findBestOption({
        stablePool,
        tokenInId,
        tokenOutId,
        slippage,
        outputAmount: amountOut,
        appproxInputAmount: bestOption.amountIn,
        minAmountMultiplier: 0.95,
        maxAmountMultiplier: 1.05,
        numberOfSteps: 200,
      });
    }
  }

  if (!bestOption) {
    throw new Error('No best option for getInputAmount');
  }

  return bestOption;
};
